import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import { siteInfo } from '@/mocks/manga';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于我们 - 刃牙漫画在线观看',
  description: '了解刃牙漫画在线观看平台，提供刃牙漫画全集免费在线阅读服务。包含品牌介绍、平台口碑及联系方式。',
  url: 'https://baki-manga.com/about',
  isPartOf: {
    '@type': 'WebSite',
    name: siteInfo.name,
    url: 'https://baki-manga.com',
  },
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: '首页', item: 'https://baki-manga.com' },
    { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://baki-manga.com/about' },
  ],
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: '刃牙漫画可以在本站免费阅读吗？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '是的，本站提供刃牙漫画全集免费在线阅读服务，无需注册即可浏览全部章节。',
      },
    },
    {
      '@type': 'Question',
      name: '本站收录了哪些刃牙漫画系列？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '本站完整收录了刃牙全系列作品：刃牙、刃牙II、範馬刃牙、刃牙道、刃牙道II以及相关外传和格斗漫画推荐。',
      },
    },
    {
      '@type': 'Question',
      name: '如何联系刃牙漫画在线观看平台？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '您可以通过本页面底部的联系表单或发送邮件至 contact@baki-manga.com 与我们取得联系。',
      },
    },
  ],
};

export default function AboutPage() {
  const FORM_SUBMIT_URL = 'https://readdy.ai/api/form/d9oo612qs38sj6jc7cd0';

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = form.querySelector<HTMLInputElement>('input[name="website_alt"]');
    if (honeypot && honeypot.value.trim()) {
      alert('提交成功！感谢您的反馈。');
      form.reset();
      return;
    }

    const formData = new FormData(form);
    formData.delete('website_alt');

    try {
      const res = await fetch(FORM_SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const text = await res.text();
      let serverMsg = '';
      try {
        const parsed = JSON.parse(text);
        serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || text;
      } catch {
        serverMsg = text;
      }
      if (res.ok && (!serverMsg || !serverMsg.includes('spam'))) {
        alert('提交成功！感谢您的反馈，我们会尽快回复。');
        form.reset();
      } else {
        alert(serverMsg || '提交失败，请稍后再试。');
      }
    } catch {
      alert('网络错误，请稍后再试。');
    }
  }

  return (
    <>
      <SEOHead
        title="关于我们"
        description="了解刃牙漫画在线观看平台，提供刃牙漫画全集免费在线阅读服务。包含品牌介绍、平台口碑及联系方式。"
        canonicalUrl="https://baki-manga.com/about"
        schema={{ ...aboutSchema, ...breadcrumbSchema, ...faqSchema }}
      />
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="bg-background-950 py-20 md:py-28">
            <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
              <span className="inline-block px-3 py-1 rounded-full bg-primary-500/20 text-primary-300 text-xs font-medium mb-4">
                关于我们
              </span>
              <h1 className="text-3xl md:text-4xl font-bold font-heading text-background-50 mb-4">
                刃牙漫画在线观看
              </h1>
              <p className="text-background-300 text-sm leading-relaxed max-w-xl mx-auto">
                致力于为刃牙漫画爱好者提供最优质的在线阅读体验，
                收录刃牙全系列及精选格斗漫画作品
              </p>
            </div>
          </div>

          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: '关于我们' },
              ]}
            />

            <article className="pb-16">
              <section className="mb-16">
                <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-6">
                  品牌介绍
                </h2>
                <div className="bg-background-100 rounded-lg p-6 md:p-8 border border-background-200/70">
                  <div className="flex flex-col md:flex-row gap-8 items-start">
                    <div className="flex-shrink-0">
                      <div className="w-24 h-24 rounded-lg bg-primary-500 flex items-center justify-center">
                        <i className="ri-sword-line text-4xl text-background-50"></i>
                      </div>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                        刃牙漫画在线观看平台创建于2020年，是国内首批专注于刃牙系列漫画的在线阅读网站。
                        我们热爱刃牙漫画所传达的不屈精神和对强大的追求，致力于为所有刃牙粉丝提供一个
                        优质的阅读平台。
                      </p>
                      <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                        平台完整收录了从板垣恵介1991年开始创作的《刃牙》第一部，到最新连载中的
                        《刃牙道II》全系列作品，以及《饿狼传》《拳愿阿修罗》等精选格斗漫画。
                        所有漫画均支持下拉式即时阅读，无需下载任何软件即可流畅浏览。
                      </p>
                      <p className="text-sm text-foreground-700 leading-relaxed">
                        我们坚持提供优质的阅读体验，所有漫画内容经过精心整理编排，确保读者能够
                        按照最佳顺序阅读刃牙系列，不错过任何一场精彩的对决。
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              <section className="mb-16">
                <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-6">
                  平台数据
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { value: '2020', label: '创立年份' },
                    { value: '12+', label: '漫画作品' },
                    { value: '400+', label: '漫画章节' },
                    { value: '1600万+', label: '累计阅读' },
                  ].map((stat) => (
                    <div
                      key={stat.label}
                      className="bg-background-100 rounded-lg p-5 text-center border border-background-200/70"
                    >
                      <div className="text-2xl md:text-3xl font-bold font-heading text-primary-600 mb-1">
                        {stat.value}
                      </div>
                      <div className="text-xs text-foreground-500">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </section>

              <section className="mb-16">
                <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-6">
                  读者口碑
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {[
                    {
                      name: '格斗迷小王',
                      text: '终于找到一个完整收录刃牙全系列的平台！下拉式阅读体验太棒了，一口气看了几十话，根本停不下来。',
                      rating: 5,
                    },
                    {
                      name: '漫画爱好者阿杰',
                      text: '刃牙漫画的画风和格斗场面真的独一无二，感谢这个平台让我能免费在线重温经典。阅读界面非常清爽干净。',
                      rating: 5,
                    },
                    {
                      name: '大学生阿强',
                      text: '被同学安利了刃牙，这个网站收录得很全，从第一部开始按顺序看，体验非常好。推荐给所有格斗漫画迷！',
                      rating: 4,
                    },
                    {
                      name: '资深漫迷老赵',
                      text: '看了十多年刃牙了，这个网站的分类做得很清晰，还能看到相关推荐，发现了不少好作品。界面也很舒服。',
                      rating: 5,
                    },
                  ].map((review) => (
                    <div
                      key={review.name}
                      className="bg-background-100 rounded-lg p-5 border border-background-200/70"
                    >
                      <div className="flex items-center gap-1 mb-2">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <i
                            key={i}
                            className={`text-sm ${i < review.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}
                          ></i>
                        ))}
                      </div>
                      <p className="text-sm text-foreground-700 leading-relaxed mb-3">
                        "{review.text}"
                      </p>
                      <span className="text-xs text-foreground-500 font-medium">—— {review.name}</span>
                    </div>
                  ))}
                </div>
              </section>

              <section>
                <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-6">
                  联系我们
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-5">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                        <i className="ri-mail-line"></i>
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h3>
                        <p className="text-sm text-foreground-600">{siteInfo.email}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                        <i className="ri-qq-line"></i>
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-foreground-900 mb-1">QQ群</h3>
                        <p className="text-sm text-foreground-600">{siteInfo.qq}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                        <i className="ri-time-line"></i>
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-foreground-900 mb-1">工作时间</h3>
                        <p className="text-sm text-foreground-600">周一至周日 9:00 - 21:00</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-background-100 rounded-lg p-6 border border-background-200/70">
                    <h3 className="text-sm font-semibold text-foreground-900 mb-4">
                      反馈与建议
                    </h3>
                    <form data-readdy-form onSubmit={handleSubmit} className="space-y-4">
                      <div>
                        <input
                          type="text"
                          name="name"
                          placeholder="您的昵称"
                          required
                          className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors"
                        />
                      </div>
                      <div>
                        <input
                          type="email"
                          name="email"
                          placeholder="电子邮箱"
                          required
                          className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors"
                        />
                      </div>
                      <div>
                        <textarea
                          name="message"
                          placeholder="请输入您的反馈或建议..."
                          rows={4}
                          maxLength={500}
                          required
                          className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors resize-none"
                        ></textarea>
                        <p className="text-[10px] text-foreground-400 mt-1 text-right">最多500字符</p>
                      </div>
                      <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden readOnly />
                      <button
                        type="submit"
                        className="w-full py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors duration-200"
                      >
                        提交反馈
                      </button>
                    </form>
                  </div>
                </div>
              </section>
            </article>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}